// Product Media Gallery - Supports Images and Videos
import { useState, useRef } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, Image, Video } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

// Check if URL is a video
const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some(ext => lowerUrl.includes(ext)) || 
         lowerUrl.includes('youtube.com') || 
         lowerUrl.includes('youtu.be') ||
         lowerUrl.includes('vimeo.com');
};

// Get YouTube embed URL
const getYouTubeEmbedUrl = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 
    ? `https://www.youtube.com/embed/${match[2]}?rel=0` 
    : null;
};

// Video Player Component
const VideoPlayer = ({ src, poster, className = '' }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(true);

  // Check if it's a YouTube URL
  const youtubeUrl = getYouTubeEmbedUrl(src);
  
  if (youtubeUrl) {
    return (
      <div className={`relative w-full h-full ${className}`}>
        <iframe
          src={youtubeUrl}
          title="Product Video"
          className="w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  return (
    <div 
      className={`relative w-full h-full bg-black ${className}`}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(isPlaying ? false : true)}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        className="w-full h-full object-contain"
        muted={isMuted}
        loop
        playsInline
        onClick={togglePlay}
      />
      
      {/* Play/Pause Overlay */}
      {!isPlaying && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer"
          onClick={togglePlay}
        >
          <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
            <Play className="w-8 h-8 text-slate-900 ml-1" />
          </div>
        </div>
      )}

      {/* Video Controls */}
      {showControls && isPlaying && (
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
          <div className="flex items-center justify-between">
            <button 
              onClick={togglePlay}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              {isPlaying ? (
                <Pause className="w-5 h-5 text-white" />
              ) : (
                <Play className="w-5 h-5 text-white" />
              )}
            </button>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={toggleMute}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                {isMuted ? (
                  <VolumeX className="w-5 h-5 text-white" />
                ) : (
                  <Volume2 className="w-5 h-5 text-white" />
                )}
              </button>
              <button 
                onClick={toggleFullscreen}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <Maximize className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Badge */}
      <div className="absolute top-4 left-4">
        <Badge className="bg-red-500 text-white gap-1">
          <Video className="w-3 h-3" />
          Video
        </Badge>
      </div>
    </div>
  );
};

// Main Media Gallery Component
export default function ProductMediaGallery({ product, isPhysical }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  // Combine all media (images + videos)
  const allMedia = [
    // Main image first
    ...(product.image_url ? [{ type: 'image', url: product.image_url }] : []),
    // Additional images
    ...(product.images || []).map(url => ({ type: 'image', url })),
    // Videos
    ...(product.videos || []).map(url => ({ type: 'video', url })),
  ].filter(item => item.url);

  // If no media, show placeholder
  if (allMedia.length === 0) {
    return (
      <div className="aspect-square rounded-lg overflow-hidden border bg-muted flex items-center justify-center">
        <div className="text-center text-muted-foreground">
          <Image className="w-16 h-16 mx-auto mb-2 opacity-50" />
          <p>No media available</p>
        </div>
      </div>
    );
  }

  const currentMedia = allMedia[selectedIndex];
  const isVideo = currentMedia.type === 'video' || isVideoUrl(currentMedia.url);

  return (
    <div className="space-y-4">
      {/* Main Media Display */}
      <div className="relative aspect-square rounded-lg overflow-hidden border bg-muted">
        {isVideo ? (
          <VideoPlayer 
            src={currentMedia.url} 
            poster={product.image_url}
          />
        ) : (
          <img
            src={currentMedia.url}
            alt={product.name}
            className="w-full h-full object-cover transition-opacity duration-300"
            data-testid="product-image"
          />
        )}
        
        {/* Product Badges */}
        {product.featured && !isVideo && (
          <div className="absolute top-4 left-4">
            <Badge className="bg-foreground text-background gap-1 px-3 py-1">
              Featured
            </Badge>
          </div>
        )}
        
        <div className="absolute top-4 right-4">
          <Badge className={`px-3 py-1 ${isPhysical ? 'badge-physical' : 'badge-affiliate'}`}>
            {isPhysical ? 'Physical' : 'Affiliate'}
          </Badge>
        </div>
        
        {/* Media counter badge */}
        {allMedia.length > 1 && (
          <div className="absolute top-14 right-4 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
            {isVideo ? <Video className="w-3 h-3" /> : <Image className="w-3 h-3" />}
            {selectedIndex + 1} / {allMedia.length}
          </div>
        )}
      </div>
      
      {/* Thumbnail Gallery */}
      {allMedia.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2" data-testid="product-gallery">
          {allMedia.map((media, index) => {
            const isThumbVideo = media.type === 'video' || isVideoUrl(media.url);
            return (
              <button
                key={index}
                onClick={() => setSelectedIndex(index)}
                className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                  selectedIndex === index 
                    ? 'border-primary ring-2 ring-primary/30' 
                    : 'border-border hover:border-primary/50'
                }`}
                data-testid={`gallery-thumb-${index}`}
              >
                {isThumbVideo ? (
                  <div className="w-full h-full bg-slate-800 flex items-center justify-center">
                    <Play className="w-6 h-6 text-white" />
                  </div>
                ) : (
                  <img
                    src={media.url}
                    alt={`${product.name} - View ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                )}
                {isThumbVideo && (
                  <div className="absolute bottom-1 right-1">
                    <Video className="w-3 h-3 text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export { isVideoUrl, VideoPlayer };
